import React from 'react';
import { CheckCircleIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/outline';

import { TwTable } from '../../../components/TailwindTable';
import { Badge, badgeTypes } from '../../../components/Badge';
import { Toggle } from '../../../components/Toggle';
import { Limited } from './importConnectionsReport';
import { whatHappens } from './importConnectionsModel';

const rowBadges = {
  create: [badgeTypes.success, 'Will be created'],
  update: [badgeTypes.info, 'Will be updated'],
  unchanged: [badgeTypes.default, 'Unchanged'],
  skipped: [badgeTypes.warn, 'Not found'],
  remove: [badgeTypes.error, 'Will be removed'],
  kept: [badgeTypes.default, 'Kept'],
};

const cards = (env) => [
  { id: 'skipped', label: `Not found in ${env}`, dot: 'bg-yellow-500', tone: 'text-yellow-700 dark:text-yellow-300', hint: 'The logic file or document does not exist. These are skipped.' },
  { id: 'remove', label: 'Will be removed', dot: 'bg-red-500', tone: 'text-red-700 dark:text-red-300', hint: 'In an imported logic file, but not in the export or listed there without a connection.' },
  { id: 'warnings', label: 'Imported with warnings', dot: 'bg-purple-500', tone: 'text-purple-700 dark:text-purple-300', hint: `A workflow, SmartForm or list is missing in ${env}. Imported as it is.` },
  { id: 'create', label: 'Will be created', dot: 'bg-blue-500', tone: 'text-blue-800 dark:text-blue-300', hint: 'The document has no connection yet. One is added.' },
  { id: 'update', label: 'Will be updated', dot: 'bg-indigo-500', tone: 'text-indigo-800 dark:text-indigo-300', hint: 'The connection exists but differs from the export.' },
  { id: 'unchanged', label: 'Unchanged', dot: 'bg-gray-400', tone: 'text-gray-700 dark:text-gray-300', hint: 'Already identical. Nothing happens.' },
];

export function SummaryCards({ summary, env, filter, onToggle }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
      {cards(env).map(card => (
        <button key={card.id} id={`review-card-${card.id}`} type="button" aria-pressed={filter === card.id} onClick={() => onToggle(card.id)}
          className={`text-left rounded-lg border p-4 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-blue ${filter === card.id
            ? 'border-indigo-600 ring-1 ring-indigo-600 dark:border-brand-blue dark:ring-brand-blue'
            : 'border-gray-200 hover:border-gray-400 dark:border-gray-700'}`}>
          <span className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-200">
            <span className={`h-2 w-2 rounded-full flex-none ${card.dot}`} aria-hidden="true" />
            {card.label}
          </span>
          <span className={`block mt-1 text-3xl font-bold ${card.tone}`}>{summary[card.id]}</span>
          <span className="block mt-1 text-xs text-gray-600 dark:text-gray-400">{card.hint}</span>
        </button>
      ))}
    </div>
  );
}

export function SourceStrip({ source, exportedText, backupText, files, totalFiles, documents, onChangeFiles }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
      <span><strong>Source:</strong> {source}</span>
      {backupText
        ? <span><strong>Backup taken:</strong> {backupText}</span>
        : exportedText && <span><strong>Exported:</strong> {exportedText}</span>}
      <span>
        <strong>Logic files:</strong> {totalFiles > files ? `${files} of ${totalFiles}` : files}
        {onChangeFiles && <> (<button id="change-logic-files" type="button" className="font-medium text-indigo-600 hover:underline dark:text-brand-blue" onClick={onChangeFiles}>Change</button>)</>}
        {' · '}<strong>Documents:</strong> {documents}
      </span>
      <span className="sm:ml-auto rounded-full bg-green-100 px-3 py-0.5 text-xs font-medium text-green-800 dark:bg-green-800 dark:text-green-100">Nothing has changed yet</span>
    </div>
  );
}

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// The export lists every document of a logic file, also those without a connection. Whether the target's connection for
// such a document goes is the person's call, so it is a switch and not buried in the list.
export function UnconnectedPanel({ unconnected, env, removeUnconnected, onChange }) {
  if (!unconnected || unconnected.documents === 0)
    return null;
  const { documents, files, connections } = unconnected;
  const effect = connections === 0
    ? `None of them has a connection in ${env}, so nothing changes either way.`
    : removeUnconnected
      ? `${plural(connections, 'connection', 'connections')} in ${env} will be removed.`
      : `${plural(connections, 'connection', 'connections')} in ${env} will be kept.`;
  return (
    <div id="unconnected-panel" className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 dark:border-indigo-700 dark:bg-indigo-900">
      <p className="text-sm font-semibold text-gray-900 dark:text-gray-200">
        {`Documents without a connection in the export: ${documents} in ${plural(files, 'logic file', 'logic files')}`}
      </p>
      <Toggle label={`Remove their connections in ${env}`} description={effect} value={removeUnconnected} onChange={onChange} id="toggle-remove-unconnected" />
    </div>
  );
}

export function CleanBill({ env }) {
  return (
    <p className="mb-4 flex items-center gap-2 text-sm text-green-800 dark:text-green-200">
      <CheckCircleIcon className="h-5 w-5 flex-none" aria-hidden="true" />
      <span>{`All logic files in the export were found in ${env}, and no connections will be removed.`}</span>
    </p>
  );
}

const chips = ({ counts, documents, withConnection, changing }) => [
  counts.skipped > 0 && [badgeTypes.warn, `${counts.skipped} not found`],
  counts.remove > 0 && [badgeTypes.error, `${counts.remove} to remove`],
  counts.kept > 0 && [badgeTypes.default, `${counts.kept} kept`],
  documents > 0 && [badgeTypes.default, plural(documents, 'document', 'documents')],
  documents > 0 && [badgeTypes.default, `${withConnection} with connection`],
  documents > 0 && [badgeTypes.default, changing > 0 ? `${changing} will change` : 'none will change'],
  counts.warnings > 0 && [badgeTypes.warn, `${counts.warnings} with warnings`],
  counts.create > 0 && [badgeTypes.success, `${counts.create} to create`],
  counts.update > 0 && [badgeTypes.info, `${counts.update} to update`],
].filter(Boolean);

const Header = ({ children, width = "" }) => <TwTable.Header additionalClass={`pl-4 uppercase ${width}`}>{children}</TwTable.Header>;
const Col = ({ children, additionalClass = '' }) => <TwTable.BodyCol additionalClass={`pl-4 py-2 align-top ${additionalClass}`}>{children}</TwTable.BodyCol>;

const Fix = ({ children }) => (
  <p className="mt-0.5 text-xs text-yellow-800 dark:text-yellow-200"><span className="font-semibold">To fix:</span> {children}</p>
);

function RowHappens({ row, env, fileNotFound }) {
  // A missing logic file is explained once above the rows, not on every document.
  if (fileNotFound)
    return <span>Skipped.</span>;
  const happens = whatHappens(row, env);
  return (
    <>
      <p>{happens.text}</p>
      {happens.fix && <Fix>{happens.fix}</Fix>}
      {happens.warnings.map(w => (
        <div key={w.text} className="mt-1">
          <p>{w.text}</p>
          <Fix>{w.fix}</Fix>
        </div>
      ))}
    </>
  );
}

export function LogicFileGroup({ group, env, open, onToggle, resetKey }) {
  const { fileName, counts, rows, fileNotFound } = group;
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;
  return (
    <section className="mb-3 rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <button type="button" aria-expanded={open} onClick={onToggle}
        className="flex w-full flex-wrap sm:flex-nowrap items-center gap-3 px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-brand-blue rounded-lg">
        <Chevron className="h-5 w-5 flex-none text-gray-500" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block text-xs uppercase text-gray-500 dark:text-gray-400">Logic file</span>
          <span className="block font-semibold text-gray-900 dark:text-gray-100 break-all">{fileName}</span>
        </span>
        <span className="flex w-full sm:w-auto flex-wrap sm:justify-end gap-2">
          {chips(group).map(([type, text]) => <Badge key={text} type={type} text={text} />)}
        </span>
      </button>
      {open && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          {fileNotFound && (
            <div className="px-4 py-3 text-sm text-yellow-800 dark:text-yellow-200">
              <p>{`The logic file is not in ${env}. ${counts.skipped} ${counts.skipped === 1 ? 'connection is' : 'connections are'} skipped.`}</p>
              <Fix>Upload the logic file, then import again.</Fix>
            </div>
          )}
          <Limited key={resetKey} items={rows}>
            {shown => (
              <div className="px-3 sm:px-1 pb-1"><TwTable additionalClass="">
                <TwTable.Head>
                  <Header width="w-28 sm:w-1/4">Document</Header>
                  <Header width="w-32 sm:w-48">Status</Header>
                  <Header width="w-72 sm:w-auto">What happens</Header>
                </TwTable.Head>
                <TwTable.Body>
                  {shown.map((row, idx) => {
                    const [type, text] = rowBadges[row.state];
                    return (
                      <TwTable.BodyRow key={`${row.documentName}|${idx}`} useHover={false}>
                        <Col additionalClass="break-all">{row.documentName}</Col>
                        <Col>
                          <div className="flex flex-wrap gap-1">
                            <Badge type={type} text={text} />
                            {row.warnings.length > 0 && <Badge type={badgeTypes.warn} text="Warning" />}
                          </div>
                        </Col>
                        <Col additionalClass="text-sm"><RowHappens row={row} env={env} fileNotFound={fileNotFound} /></Col>
                      </TwTable.BodyRow>
                    );
                  })}
                </TwTable.Body>
              </TwTable></div>
            )}
          </Limited>
        </div>
      )}
    </section>
  );
}
